import styled from "styled-components";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { useEffect, useMemo, useState } from "react";
import ExpandAndCollapseTitleSection from "../../molecules/ExpandAndCollapseTitleSection";
import { useFile } from "../../../graphql/hooks/useFile";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import { Row } from "react-grid-system";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import {
  ChevronLeftIconLarge,
  ChevronRightIconLarge,
  ChevronSmallLeftIcon,
  ChevronSmallRightIcon,
} from "../../atoms/icons/ChevronIcons";
import { DownloadIcon } from "../../atoms/icons/DownloadIcon";
import { Document, Page } from "react-pdf";
import BodySmall from "../../atoms/typography/BodySmall";
import Subheading from "../../atoms/typography/Subheading";
import IconButton from "../../atoms/buttons/IconButton";
import Checkbox from "../../atoms/inputs/Checkbox";
import FilterChip from "../../atoms/inputs/FilterChip";
import PdfTemplate from "../PdfTemplate";
import { useInvoiceReaderTemplate } from "../../../graphql/hooks/invoiceInterpretationTemplates/useInvoiceReaderTemplate";
import {
  findAllPagesForField,
  findPageForPosition,
} from "../../../utils/templateHelpers";
import SidePanel from "../../molecules/SidePanel";

interface Props {
  fileId: string;
  isGridView?: boolean;
  noBorder?: boolean;
  invoiceName?: string;
  collapsible?: boolean;
  onClickPreviousInvoice?: () => void;
  onClickNextInvoice?: () => void;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  templateId?: string;
  clientIdentifier?: string;
}

const GreyOutlinedContainer = styled.div<{ noBorder?: boolean }>`
  padding: ${(props) => (props.noBorder ? 0 : "16px")};
  border-radius: 12px;
  border: ${(props) =>
    props.noBorder ? "none" : `1px solid ${theme.colors.grey05}`};
`;

const DocumentContainer = styled.div`
  display: flex;
  justify-content: center;
  & > div {
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const InvoiceViewColumn: React.FC<Props> = ({
  fileId,
  isGridView,
  noBorder,
  invoiceName,
  collapsible,
  onClickPreviousInvoice,
  onClickNextInvoice,
  isSelected,
  onToggleSelect,
  templateId,
  clientIdentifier,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { file, error, loading } = useFile(fileId);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewNumPages, setPreviewNumPages] = useState<number>(1);

  const { template, loading: templateLoading } = useInvoiceReaderTemplate(
    clientIdentifier,
    templateId,
  );

  function base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file?.content && file?.contentType === "application/pdf") {
      try {
        const base64 = file.content.split(",").pop() || file.content;
        const byteArray = base64ToUint8Array(base64);
        const blob = new Blob([byteArray.buffer as ArrayBuffer], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(blob);

        setPdfUrl(url);

        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (error) {
        console.error("Failed to decode Base64 PDF content:", error);
        setPdfUrl(null);
      }
    } else {
      setPdfUrl(null);
    }
  }, [file]);

  const memoizedFile = useMemo(() => pdfUrl, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const handleDownload = () => {
    if (!file?.content || !file.contentType) {
      console.error("Missing file content or contentType.");
      return;
    }

    try {
      // Clean and decode Base64
      const base64 = file.content.split(",").pop() || "";
      const binaryString = atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create a Blob
      const blob = new Blob([bytes], { type: file.contentType });

      // Create Blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.fileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const maskOverlays = (template?.extraction?.maskingData ?? []).flatMap(
    (mask, index) => {
      const possibleFieldValues =
        template?.extraction?.possibleFieldValues ?? [];

      if (mask.maskingType === "REMOVE_LABEL_AND_VALUE") {
        const overlays = [];
        if (mask.label?.position) {
          const labelPages = findAllPagesForField(
            mask.label.value,
            mask.label.position,
            possibleFieldValues,
          );
          overlays.push({
            id: `mask-${index}-label`,
            position: mask.label.position,
            showOnAllPages: false,
            ...(labelPages.length > 0
              ? { showOnPages: labelPages }
              : {
                  pageNumber: findPageForPosition(
                    mask.label.position,
                    possibleFieldValues,
                  ),
                }),
            onChange: () => {},
          });
        }
        if (mask.value?.position) {
          const valuePages = findAllPagesForField(
            mask.value.value,
            mask.value.position,
            possibleFieldValues,
          );
          overlays.push({
            id: `mask-${index}-value`,
            position: mask.value.position,
            showOnAllPages: false,
            ...(valuePages.length > 0
              ? { showOnPages: valuePages }
              : {
                  pageNumber: findPageForPosition(
                    mask.value.position,
                    possibleFieldValues,
                  ),
                }),
            onChange: () => {},
          });
        }
        return overlays;
      }
      if (mask.value?.position == null) return [];
      const labelPages =
        mask.maskingType ===
          "REMOVE_ABSOLUTE_POSITION_ONLY_WHERE_LABEL_EXISTS" &&
        mask.label?.position
          ? findAllPagesForField(
              mask.label.value,
              mask.label.position,
              possibleFieldValues,
            )
          : [];
      return [
        {
          id: `mask-${index}`,
          position: mask.value.position,
          showOnAllPages:
            mask.maskingType === "REMOVE_ABSOLUTE_POSITION_ON_EVERY_PAGE",
          ...(labelPages.length > 0
            ? { showOnPages: labelPages }
            : mask.maskingType ===
                  "REMOVE_ABSOLUTE_POSITION_ONLY_WHERE_LABEL_EXISTS" &&
                mask.label?.position
              ? {
                  pageNumber: findPageForPosition(
                    mask.label.position,
                    possibleFieldValues,
                  ),
                }
              : {}),
          onChange: () => {},
        },
      ];
    },
  );

  return (
    <div style={{ flexShrink: 0, width: isGridView ? undefined : "100%" }}>
      <SidePanel
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title={t("common.preview")}
        width="fit-content"
        headerActions={
          previewNumPages > 1 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <IconButton
                icon={
                  <ChevronSmallLeftIcon color={theme.colors.textSecondary} />
                }
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                size="small"
                color={theme.colors.primaryBg}
              />
              <BodySmall color={theme.colors.textSecondary}>
                {currentPage} / {previewNumPages}
              </BodySmall>
              <IconButton
                icon={
                  <ChevronSmallRightIcon color={theme.colors.textSecondary} />
                }
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, previewNumPages))
                }
                size="small"
                color={theme.colors.primaryBg}
              />
            </div>
          ) : undefined
        }
      >
        {templateLoading ? (
          <LoadingSpinner />
        ) : (
          <div>
            <BodySmall color={theme.colors.textSecondary}>
              Nedan visas en förhandsgranskning av fakturan med maskning och
              stämpel från vald mall.
              <br />
              Alternativa stämplar renderas ej.
            </BodySmall>
            <HorizontalSpacer spacing={0.5} />
            <PdfTemplate
              fileBase64={(file?.content ?? "").split(",").pop() ?? ""}
              possibleFields={[]}
              displayHighlights={true}
              currentPage={currentPage}
              onNumPagesChange={setPreviewNumPages}
              maskOverlays={maskOverlays}
              maskOverlaysDraggable={false}
              stampInformation={
                template?.extraction?.stampInformation
                  ? {
                      ...template.extraction.stampInformation,
                      alternativeStampPositions: [],
                    }
                  : undefined
              }
            />
          </div>
        )}
      </SidePanel>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {onClickPreviousInvoice && (
          <IconButton
            icon={<ChevronSmallLeftIcon />}
            color={theme.colors.primaryBg}
            onClick={onClickPreviousInvoice}
            size="small"
          />
        )}

        {collapsible ? (
          <div style={{ flex: 1 }}>
            <ExpandAndCollapseTitleSection
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
              labelKey="invoices.invoice"
              labelSuffix={invoiceName}
            />
          </div>
        ) : (
          <Subheading color={theme.colors.primary}>
            {t("invoices.invoice")}
            {invoiceName ? ` ${invoiceName}` : ""}
          </Subheading>
        )}

        {onClickNextInvoice && (
          <IconButton
            icon={<ChevronSmallRightIcon />}
            color={theme.colors.primaryBg}
            onClick={onClickNextInvoice}
            size="small"
          />
        )}

        {onToggleSelect !== undefined && (
          <div style={{ marginLeft: "auto" }}>
            <Checkbox checked={isSelected ?? false} onChange={onToggleSelect} />
          </div>
        )}
      </div>
      {(!collapsible || isExpanded) && (
        <>
          <HorizontalSpacer spacing={1} />
          <GreyOutlinedContainer noBorder={noBorder}>
            <div
              style={{
                width: isGridView ? undefined : "10%",
                overflow: "clip",
              }}
            >
              {error && (
                <div>
                  {/*<Alert
                  type="error"
                  title={t(
                    "documentHandler.thereWasAnErrorFetchingTheDocument",
                  )}
                  message={error?.toString()}
                />*/}
                  Kunde ej hämta fil
                </div>
              )}
            </div>
            {loading && <LoadingSpinner />}
            {pdfUrl && (
              <div>
                <Row justify="between" align="center" nogutter>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    {numPages && numPages > 1 && currentPage > 1 && (
                      <OutlinedButton
                        icon={
                          <ChevronLeftIconLarge
                            color={
                              currentPage <= 1
                                ? theme.colors.white
                                : theme.colors.text
                            }
                          />
                        }
                        title={t("documentHandler.previousPage")}
                        disabled={currentPage <= 1}
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        color={theme.colors.text}
                      />
                    )}
                    <BodySmall>
                      {t("common.page")} {currentPage} av {numPages}
                    </BodySmall>
                    {numPages && numPages > 1 && (
                      <OutlinedButton
                        iconRight={
                          <ChevronRightIconLarge
                            color={
                              currentPage >= (numPages ?? 1)
                                ? theme.colors.white
                                : theme.colors.text
                            }
                          />
                        }
                        disabled={numPages ? currentPage >= numPages : true}
                        title={t("documentHandler.nextPage")}
                        color={theme.colors.text}
                        onClick={() =>
                          setCurrentPage((prev) =>
                            numPages ? Math.min(prev + 1, numPages) : prev + 1,
                          )
                        }
                      />
                    )}
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 16 }}
                  >
                    {templateId && clientIdentifier && (
                      <FilterChip
                        titleKey="Visa maskad"
                        selected={isPreviewOpen}
                        onSelect={() => setIsPreviewOpen((prev) => !prev)}
                      />
                    )}
                    <OutlinedButton
                      title={t("common.download")}
                      icon={<DownloadIcon color={theme.colors.primary} />}
                      onClick={handleDownload}
                      color={theme.colors.primary}
                    />
                  </div>
                </Row>
                <HorizontalSpacer spacing={1} />

                <DocumentContainer>
                  <Document
                    key={fileId}
                    file={memoizedFile}
                    onLoadError={(err) => console.error("PDF load error:", err)}
                    onLoadSuccess={onDocumentLoadSuccess}
                  >
                    <Page
                      pageNumber={currentPage}
                      devicePixelRatio={window.devicePixelRatio}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  </Document>
                </DocumentContainer>
              </div>
            )}
          </GreyOutlinedContainer>
        </>
      )}
    </div>
  );
};

export default InvoiceViewColumn;
